import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete, Group } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const GroupDiscountsTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    minPassengers: 1,
    maxPassengers: 1,
    discountPercentage: 0,
    label: '',
    isActive: true,
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        minPassengers: item.minPassengers,
        maxPassengers: item.maxPassengers,
        discountPercentage: item.discountPercentage,
        label: item.label,
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      setFormData({
        minPassengers: 1,
        maxPassengers: 1,
        discountPercentage: 0,
        label: '',
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/group-discounts/${editingItem.id}`,
          formData
        );
        onSuccess('Group discount updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/group-discounts`, formData);
        onSuccess('Group discount created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this group discount?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/group-discounts/${id}`);
        onSuccess('Group discount deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Group Discounts Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Group Discount
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Passenger Range</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group fontSize="small" color="action" />
                    <Typography>
                      {item.minPassengers}-{item.maxPassengers} passengers
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="success.main">
                    {item.discountPercentage.toFixed(0)}% off
                  </Typography>
                </TableCell>
                <TableCell>{item.label}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Group Discount' : 'Add Group Discount'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Minimum Passengers"
              type="number"
              value={formData.minPassengers}
              onChange={(e) =>
                setFormData({ ...formData, minPassengers: parseInt(e.target.value) })
              }
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Maximum Passengers"
              type="number"
              value={formData.maxPassengers}
              onChange={(e) =>
                setFormData({ ...formData, maxPassengers: parseInt(e.target.value) })
              }
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Discount Percentage (%)"
              type="number"
              value={formData.discountPercentage}
              onChange={(e) =>
                setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              fullWidth
            />
            <TextField
              label="Label"
              value={formData.label}
              onChange={(e) =>
                setFormData({ ...formData, label: e.target.value })
              }
              fullWidth
              helperText="Descriptive label (e.g., 'Small Group', 'Large Group')"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDiscountsTab;
